import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet';
import Footer from '../homepage/footer';
import { renderToString } from 'katex';
import 'katex/dist/katex.min.css';

// Array of all formulas categorized by topics
const allFormulas = [
    {
        title: 'Number',
        formulas: [
            { text: 'Multiplying surds', equation: '\\sqrt{a} \\times \\sqrt{b} = \\sqrt{a \\times b}' },
            { text: 'Converting units - Length', equation: '1 \\text{ kilometre} = 1000 \\text{ metres}, \\ 1 \\text{ metre} = 100 \\text{ centimetres}, \\ 1 \\text{ centimetre} = 10 \\text{ millimetres}' },
            { text: 'Converting units - Mass', equation: '1 \\text{ tonne} = 1000 \\text{ kilograms}, \\ 1 \\text{ kilogram} = 1000 \\text{ grams}' },
            { text: 'Error intervals', equation: 'a \\leq x < b', },
            { text: 'Standard form', equation: 'a \\times 10^n \\ (1 \\leq a < 10, \\ n \\in \\mathbb{Z})' },
            { text: 'Standard units - Time', equation: ' 1 \\text{ year} = 52 \\text{ weeks}, \\ 1 \\text{ day} = 24 \\text{ hours}, \\ 1 \\text{ hour} = 60 \\text{ minutes}, \\ 1 \\text{ minute} = 60 \\text{ seconds}' },
        ],
    },
    {
        title: 'Algebra',
        formulas: [
            { text: 'Laws of indices', equation: 'a^m \\times a^n = a^{m+n}, \\ \\frac{a^m}{a^n} = a^{m-n}, \\ (a^m)^n = a^{m \\times n}' },
            { text: 'Standard graphs', equation: '\\text{Includes } y = x, \\ y = x^2, \\ y = x^3, \\ y = \\frac{1}{x}, \\ y = \\sqrt{x}' },
            { text: 'Equation of a straight line', equation: 'y = mx + c \\ (\\text{m is the gradient, c is the y-intercept})' },
            { text: 'Expanding brackets', equation: '(a + b)^2 = a^2 + 2ab + b^2, \\ (a - b)^2 = a^2 - 2ab + b^2' },
            { text: 'Quadratic equation', equation: 'ax^2 + bx + c = 0' },
            { text: 'Difference of two squares', equation: 'a^2 - b^2 = (a + b)(a - b)' },
            { text: 'Simultaneous equations', equation: '\\text{Solve } \\begin{cases} x + y = 5 \\\\ x - y = 3 \\end{cases} \\text{ by substitution or elimination}' },
            { text: 'Arithmetic sequence (nth term)', equation: 'a_n = a + (n - 1)d' },
            { text: 'Geometric sequence (nth term)', equation: 'a_n = ar^{n-1}' },
            { text: 'Fibonacci sequence', equation: '\\text{Each term is the sum of the previous two terms}' },
            { text: 'Triangle numbers', equation: 'T_n = \\frac{n(n + 1)}{2} \\ \\text{(e.g., } 1, 3, 6, 10, 15, \\dots\\text{)}' },
            { text: '* Completing the square', equation: 'a(x + p)^2 + q \\ \\text{to find turning points or solve quadratic equations}', isHigher: true },
            { text: '* Quadratic formula', equation: 'x = \\frac{-b \\pm \\sqrt{b^2 - 4ac}}{2a}', isHigher: true },
            { text: '* Equation of a circle', equation: 'x^2 + y^2 = r^2 \\ \\text{(circle with center (0, 0) and radius r)}', isHigher: true },
            { text: '* Velocity-time graphs', equation: '\\text{Gradient = acceleration; area under curve = distance}', isHigher: true },
        ],
    },
    {
        title: 'Ratio, Proportion and Rates of Change',
        formulas: [
            { text: 'Gradient', equation: 'm = \\frac{\\Delta y}{\\Delta x}' },
            { text: 'Speed', equation: '\\text{Speed} = \\frac{\\text{Distance}}{\\text{Time}}' },
            { text: 'Density', equation: '\\text{Density} = \\frac{\\text{Mass}}{\\text{Volume}}' },
            { text: 'Pressure', equation: '\\text{Pressure} = \\frac{\\text{Force}}{\\text{Area}}' },
            { text: 'Two lines are perpendicular', equation: 'm_1 \\times m_2 = -1' },
            { text: 'Linear Scale Factor (Enlargement)', equation: '\\text{Large number} \\div \\text{Small number}' },
            { text: 'Linear Scale Factor (Reduction)', equation: '\\text{Small number} \\div \\text{Large number}' },
            { text: 'Percentage of x', equation: '\\text{y percent of x} = \\frac{y}{100} \\times x' },
            { text: 'Find Percentage Increase', equation: '\\frac{\\text{Increase}}{\\text{Original}} \\times 100' },
            { text: '* Percentage multipliers', equation: '\\text{New Value} = \\text{Original Value} \\times (1 \\pm \\text{Percentage Change})^n', isHigher: true },
            { text: '* Compound interest', equation: 'A = P \\left(1 + \\frac{r}{100}\\right)^n', isHigher: true },
            { text: '* Direct proportion', equation: 'y \\propto x \\ \\text{or} \\ y = kx', isHigher: true },
            { text: '* Direct proportion to a power', equation: 'y \\propto x^n \\ \\text{or} \\ y = kx^n', isHigher: true },
            { text: '* Inverse proportion', equation: 'y \\propto \\frac{1}{x} \\ \\text{or} \\ y = \\frac{k}{x}', isHigher: true },
        ],
    },
    {
        title: 'Geometry',
        formulas: [
            { text: 'Area of a rectangle', equation: 'A = l \\times w' },
            { text: 'Area of a triangle', equation: 'A = \\frac{1}{2} \\times b \\times h' },
            { text: 'Area of a trapezium', equation: 'A = \\frac{1}{2} \\times (a + b) \\times h' },
            { text: 'Volume of a cuboid', equation: 'V = l \\times w \\times h' },
            { text: 'Circumference of a circle', equation: 'C = \\pi \\times r' },
            { text: 'Area of a circle', equation: 'A = \\pi r^2' },
            { text: 'Arc Length', equation: '\\text{Arc Length} = \\frac{\\theta}{360} \\times 2 \\pi r' },
            { text: 'Area of a Sector', equation: '\\text{Area of Sector} = \\frac{\\theta}{360} \\times \\pi r^2' },
            { text: 'Volume of a Cylinder', equation: 'V = \\pi r^2 \\times \\text{height}' },
            { text: 'Volume of a Prism', equation: 'V = \\text{Area of cross-section} \\times \\text{length}' },
            { text: 'Interior Angles in a Triangle', equation: '\\text{Sum of Interior Angles} = 180^\\circ' },
            { text: 'Interior Angles of Any Polygon', equation: '\\text{Sum} = (n - 2) \\times 180^\\circ' },
            { text: 'Exterior Angle of a Regular Polygon', equation: '\\text{Exterior Angle} = \\frac{360}{n}' },
            { text: 'Angles on a Straight Line', equation: '\\text{Sum} = 180^\\circ' },
            { text: 'Angles in a Full Turn', equation: '\\text{Sum} = 360^\\circ' },
            { text: "Pythagoras' Theorem", equation: 'a^2 + b^2 = c^2' },
            { text: 'Trigonometric Ratios', equation: '\\sin \\theta = \\frac{\\text{opposite}}{\\text{hypotenuse}}, \\ \\cos \\theta = \\frac{\\text{adjacent}}{\\text{hypotenuse}}, \\ \\tan \\theta = \\frac{\\text{opposite}}{\\text{adjacent}}' },
            { text: '* Sine Rule', equation: '\\frac{a}{\\sin A} = \\frac{b}{\\sin B} = \\frac{c}{\\sin C}', isHigher: true },
            { text: '* Cosine Rule (Finding a side)', equation: 'a^2 = b^2 + c^2 - 2bc \\cos A', isHigher: true },
            { text: '* Cosine Rule (Finding an angle)', equation: '\\cos A = \\frac{b^2 + c^2 - a^2}{2bc}', isHigher: true },
            { text: '* Area of a Triangle using Sine', equation: 'A = \\frac{1}{2} ab \\sin C', isHigher: true },
            { text: '* Volume of a Cone', equation: 'V = \\frac{1}{3} \\pi r^2 h', isHigher: true },
            { text: '* Volume of a Pyramid', equation: 'V = \\frac{1}{3} \\text{Area of Base} \\times \\text{Height}', isHigher: true },
            { text: '* Surface Area of a Sphere', equation: 'A = 4 \\pi r^2', isHigher: true },
            { text: '* Volume of a Sphere', equation: 'V = \\frac{4}{3} \\pi r^3', isHigher: true },
            { text: '* Similar Shapes', equation: '\\text{Length/Perimeter Ratio: } 1 : n, \\ \\text{Area Ratio: } 1 : n^2, \\ \\text{Volume Ratio: } 1 : n^3', isHigher: true },
        ],
    },
    {
        title: 'Probability',
        formulas: [
            { text: 'Probability', equation: '\\text{Probability} = \\frac{\\text{Number of actual outcomes}}{\\text{Number of possible outcomes}}' },
            { text: 'Probability of an event being certain', equation: 'P(\\text{certain}) = 1' },
            { text: 'Probability of an event being impossible', equation: 'P(\\text{impossible}) = 0' },
            { text: 'Probability of an event not occurring', equation: 'P(A^\\prime) = 1 - P(A)' },
            { text: 'Sum of probabilities', equation: 'P(A) + P(A^\\prime) = 1' },
            { text: 'Independent events', equation: 'P(A \\cap B) = P(A) \\times P(B)' },
            { text: '* Mutually Exclusive Events', equation: 'P(A \\text{ or } B) = P(A) + P(B)', isHigher: true }
        ],
    },
    {
        title: 'Statistics',
        formulas: [
            { text: 'Mean (ungrouped data)', equation: '\\text{Mean} = \\frac{\\text{Total of values}}{\\text{Number of values}}' },
            { text: 'Mean (grouped data)', equation: '\\text{Mean} = \\frac{\\text{Sum of frequency × midpoint}}{\\text{Sum of frequencies}}' },
            { text: 'Median', equation: 'Median = \\frac{1}{2} \\text{ of the values}' },
            { text: 'Mode', equation: '\\text{Mode = Most frequently occurring value}' },
            { text: 'Stratified Sampling', equation: '\\text{Amount} = \\frac{\\text{Group Number}}{\\text{Total}} \\times \\text{Sample Size}' },
            { text: '* Quartiles and Interquartile Range', equation: 'LQ = \\frac{1}{4} \\text{ of the data}, \\ UQ = \\frac{3}{4} \\text{ of the data}, \\ IQR = UQ - LQ', isHigher: true },
            { text: '* Frequency Density', equation: '\\text{Frequency Density} = \\frac{\\text{Frequency}}{\\text{Class Width}}', isHigher: true },
        ],
    },
];

// Helper component to render LaTeX equations using KaTeX
const LatexEquation = ({ equation, className = '', style = {} }) => {
    const html = useMemo(() => {
        try {
            return renderToString(equation || "", { 
                throwOnError: false, 
                displayMode: true 
            });
        } catch {
            return equation || "";
        }
    }, [equation]);

    return (
        <div 
            className={className}
            style={{
                ...style,
                overflowX: 'auto',
                overflowY: 'visible',
                minWidth: 0
            }}
            dangerouslySetInnerHTML={{ __html: html }}
        />
    );
};

const Formula = () => {
    return (
        <div className="flex flex-col min-h-screen bg-gray-50">
            <Helmet>
                <title>Math Formulas | MLMaths</title>
                <link rel="canonical" href="https://mlmaths.co.uk/formula" />
                <meta name="description" content="Browse essential GCSE Maths formulas organized by topic including algebra, geometry, statistics, and more." />
            </Helmet>

            <main className="container mx-auto py-10 px-4 sm:px-6 lg:px-12 max-w-7xl xl:max-w-[95rem] flex-grow">
                {/* Header Section */}
                <div className="text-center mb-10">
                    <div className="bg-teal-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-10 h-10 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                    </div>
                    <h1 className="text-4xl font-bold text-gray-800 mb-2">Math Formulas</h1>
                    <p className="text-lg text-gray-600">Essential GCSE Maths formulas for quick reference</p>
                </div>

                {/* Formulas Section */}
                <div className="space-y-8">
                    {allFormulas.map((section, index) => (
                        <div key={index} className="bg-white rounded-2xl shadow-xl overflow-hidden border-t-4 border-teal-500">
                            {/* Section Header */}
                            <div className="bg-gradient-to-r from-teal-500 to-teal-600 p-6">
                                <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                    </svg>
                                    {section.title}
                                </h2>
                                {section.formulas.some(f => f.isHigher) && (
                                    <p className="text-teal-100 text-sm mt-2">* indicates Higher tier only</p>
                                )}
                            </div>

                            {/* Formulas Grid */}
                            <div className="p-6 xl:p-8">
                                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 xl:gap-6">
                                    {section.formulas.map((formula, idx) => (
                                        <div key={idx} className="border-2 border-gray-100 rounded-xl p-4 xl:p-6 hover:border-teal-200 transition-all bg-gray-50 min-w-0 overflow-hidden">
                                            {/* Mobile/Tablet (up to xl): Horizontal scroll wrapper */}
                                            <div className="xl:hidden overflow-x-auto overflow-y-visible w-full" style={{scrollbarWidth: 'thin'}}>
                                                <div style={{minWidth: 'max-content'}}>
                                                    <p className={`text-gray-700 font-medium text-sm mb-3 ${formula.isHigher ? 'font-bold text-teal-700' : ''}`}>
                                                        {formula.text}
                                                    </p>
                                                    <LatexEquation 
                                                        equation={formula.equation}
                                                        className={`text-teal-600 text-sm ${formula.isHigher ? 'font-bold' : ''}`}
                                                        style={{minWidth: 'max-content'}}
                                                    />
                                                </div>
                                            </div>
                                            
                                            {/* Desktop (xl and above): Normal display with wrapping */}
                                            <div className="hidden xl:block w-full min-w-0">
                                                <p className={`text-gray-700 font-medium text-base xl:text-lg mb-3 xl:mb-4 break-words ${formula.isHigher ? 'font-bold text-teal-700' : ''}`}>
                                                    {formula.text}
                                                </p>
                                                <div className="w-full overflow-x-auto overflow-y-visible" style={{scrollbarWidth: 'thin'}}>
                                                    <div style={{minWidth: 'max-content', display: 'inline-block'}}>
                                                        <LatexEquation 
                                                            equation={formula.equation}
                                                            className={`text-teal-600 text-base xl:text-lg ${formula.isHigher ? 'font-bold' : ''}`}
                                                            style={{display: 'block'}}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default Formula;